import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { format } from "date-fns";
import { Box, Checkbox, Chip, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "convertupleads-theme";
import { deleteLog, deselectAllLogs, getLogs, selectAllLogs, selectLog } from "../../state/log/logSlice";
import { selectLogState } from "../../state/log/logSelector";
import GlobalTablePagination from "../../global/Pagination/GlobalTablePagination";
import { Delete } from "@mui/icons-material";
import TableLoaderSkeleton from "../../global/Loader/TableLoaderSkeleton";
import GlobalEmptyPage from "../../common/GlobalEmptyPage/GlobalEmptyPage";
import EmptyBoxLogo from "../../assets/images/inbox-empty.png";

const headCells = [
  {
    id: "level",
    label: "Level",
  },
  {
    id: "createdAt",
    label: "Created At",
  },
  {
    id: "message",
    label: "Message",
  },
  {
    id: "meta",
    label: "Meta Info",
  },
];

const TableHeader = ({ numSelected, onSelectAllClick, rowCount }) => {
  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox" sx={{ width: "40px !important" }}>
          <Checkbox
            size="small"
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              "aria-label": "select all desserts",
            }}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell key={headCell.id} align="left" padding={"normal"}>
            {headCell.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

const LogTable = () => {
  const dispatch = useDispatch();
  const {
    data: logs,
    isLoading = false,
    page,
    perPage,
    count,
    renderId,
    selectedIds,
    filters: { searchText, level, date },
  } = useSelector(selectLogState);

  useEffect(() => {
    dispatch(getLogs({ page: renderId ? page : 1, perPage: perPage, searchText, level, date }));
  }, [dispatch, perPage, searchText, level, date, renderId]);

  const onChangePage = (newPage) => {
    dispatch(getLogs({ page: newPage, perPage: perPage, level, searchText, date }));
  };

  const onChangePerPage = (perPage, newPage) => {
    dispatch(getLogs({ page: newPage, perPage: perPage, level, searchText, date }));
  };
  const handleDeleteLog = (id) => {
    if (window.confirm("Are you sure to delete this log?")) {
      dispatch(deleteLog({ ids: [id] }));
    }
  };

  const numSelected = selectedIds.length;
  const rowCount = logs.length;

  const onSelectAllClick = () => {
    if (numSelected === rowCount) {
      dispatch(deselectAllLogs());
    } else {
      dispatch(selectAllLogs());
    }
  };

  const handleClick = (id) => {
    dispatch(selectLog(id));
  };

  let content = null;
  if (isLoading) {
    content = <TableLoaderSkeleton columns={5} numberOfRows={15} />;
  } else if (logs?.length === 0) {
    content = <GlobalEmptyPage image={EmptyBoxLogo} title={"NO LOGS FOUND!"} />;
  } else if (logs?.length > 0) {
    content = (
      <Box sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: `calc(100vh - 190px)` }}>
          <Table variant={"bordered"} dense size="small" stickyHeader aria-label="sticky table">
            <TableHeader numSelected={numSelected} onSelectAllClick={onSelectAllClick} rowCount={rowCount} />
            <TableBody>
              {logs.map((logInfo) => {
                const { id, message, meta, level, createdAt } = logInfo || {};

                const createdAtDate = `${format(new Date(createdAt), "d MMM yyyy")} ${format(new Date(createdAt), "hh:mm a")}`;
                const isItemSelected = selectedIds.indexOf(id) !== -1;

                return (
                  <TableRow key={logInfo.id} hover onClick={() => handleClick(id)} role="checkbox" aria-checked={isItemSelected} tabIndex={-1} selected={isItemSelected} sx={{ cursor: "pointer" }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        color="primary"
                        checked={isItemSelected}
                        inputProps={{
                          "aria-labelledby": level + id,
                        }}
                      />
                    </TableCell>
                    <TableCell align="left">
                      <Chip size={"small"} label={level} color={level === "error" ? "error" : level === "warn" ? "warning" : "info"} />
                    </TableCell>
                    <TableCell align="left">{createdAtDate}</TableCell>
                    <TableCell
                      align="left"
                      maxWidth={400}
                      sx={{
                        maxWidth: 400,
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                      }}
                    >
                      {message}
                    </TableCell>
                    <TableCell align="left" maxWidth={400} sx={{ maxWidth: 400, whiteSpace: "normal", wordBreak: "break-word" }}>
                      {meta ? JSON.stringify(meta) : "-"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <GlobalTablePagination count={count} page={page} rowsPerPage={perPage} onChangePerPage={onChangePerPage} onChangePage={onChangePage} />
      </Box>
    );
  }
  return content;
};

export default LogTable;
